/**
 * THE EXPIRY VOCABULARY — presentation only.
 *
 * The *state* of a row is decided on the server (utils/expiry.js) and arrives on
 * every product as `expiry_state` + `days_to_expiry`. This file never recomputes
 * it: a browser with a wrong clock must not be able to paint a stale shelf green.
 *
 * What lives here is how that state LOOKS and READS — the colour tokens, the
 * label, and the sentence that tells a human what to do about it. Defined once,
 * so the badge, the card edge, the dashboard tile, the timeline bar and the
 * filter chip can never disagree.
 */
import { AlertOctagon, AlertTriangle, CalendarClock, CircleHelp, Clock, ShieldCheck } from 'lucide-react';

export const EXPIRY_STATES = ['expired', 'critical', 'warning', 'watch', 'fresh', 'unknown'];

/**
 * The pseudo-state for stock that has been stocked out and is on order. It is
 * a `status`, not an expiry state, so it travels as its own filter value.
 */
export const AWAITING = 'awaiting';

/** The states that put a row on someone's to-do list — mirrors the server's list. */
export const ACTIONABLE_STATES = ['expired', 'critical', 'warning'];

const META = {
  expired: {
    label: 'Expired',
    short: 'Expired',
    icon: AlertOctagon,
    tone: 'error',
    blurb: 'Past its date — pull it off the shelf.',
  },
  critical: {
    label: 'Expiring now',
    short: 'Critical',
    icon: AlertTriangle,
    tone: 'error',
    blurb: 'Inside the critical window — move it today.',
  },
  warning: {
    label: 'Expiring soon',
    short: 'Soon',
    icon: Clock,
    tone: 'warning',
    blurb: 'Inside the warning window — plan for it.',
  },
  watch: {
    label: 'On the horizon',
    short: 'Watch',
    icon: CalendarClock,
    tone: 'info',
    blurb: 'Coming up, but nothing to do yet.',
  },
  fresh: {
    label: 'Fresh',
    short: 'Fresh',
    icon: ShieldCheck,
    tone: 'success',
    blurb: 'Well within its shelf life.',
  },
  unknown: {
    label: 'No date',
    short: 'No date',
    icon: CircleHelp,
    tone: 'default',
    blurb: 'No expiry date recorded — add one so it can be tracked.',
  },
};

/** Everything the UI knows about a state, including its four CSS variables. */
export function expiryMeta(state) {
  const key = META[state] ? state : 'unknown';
  const m = META[key];
  return {
    key,
    ...m,
    fg: `var(--exp-${key}-fg)`,
    bg: `var(--exp-${key}-bg)`,
    border: `var(--exp-${key}-br)`,
    solid: `var(--exp-${key}-solid)`,
  };
}

/**
 * The day count as a human reads it.
 *
 *   -3 → "3 days ago"      0 → "today"      1 → "tomorrow"      12 → "in 12 days"
 *
 * Past 60 days it switches to months, because "in 428 days" is a number nobody
 * converts in their head.
 */
export function expiryPhrase(days) {
  if (days == null) return 'No expiry date';
  if (days === 0) return 'Expires today';
  if (days === 1) return 'Expires tomorrow';
  if (days === -1) return 'Expired yesterday';
  if (days < 0) {
    const past = Math.abs(days);
    return past <= 60 ? `Expired ${past} days ago` : `Expired ${Math.round(past / 30)} months ago`;
  }
  return days <= 60 ? `Expires in ${days} days` : `Expires in ${Math.round(days / 30)} months`;
}

/** The compact form for a table cell or a card corner: "−12d", "6d", "14mo". */
export function expiryShort(days) {
  if (days == null) return '—';
  if (days < 0) return Math.abs(days) <= 99 ? `−${Math.abs(days)}d` : `−${Math.round(Math.abs(days) / 30)}mo`;
  return days <= 99 ? `${days}d` : `${Math.round(days / 30)}mo`;
}

/** The default policy, used only until the API's own numbers arrive. */
export const DEFAULT_POLICY = { criticalDays: 7, warningDays: 30 };

/**
 * The state a given day-count WOULD produce under a policy.
 *
 * Used for one thing only: previewing a date the user is still typing, before
 * anything is saved. The server remains the authority for every state that is
 * actually stored and displayed — this exists so the expiry editor can say
 * "this makes it critical" while the picker is still open, and it takes the
 * warehouse's real thresholds so it cannot say something the server would then
 * contradict.
 */
export function previewState(days, policy = DEFAULT_POLICY) {
  if (days == null) return 'unknown';
  const critical = policy?.criticalDays ?? DEFAULT_POLICY.criticalDays;
  const warning = policy?.warningDays ?? DEFAULT_POLICY.warningDays;
  if (days < 0) return 'expired';
  if (days <= critical) return 'critical';
  if (days <= warning) return 'warning';
  if (days <= warning * 3) return 'watch';
  return 'fresh';
}

/**
 * The filter chips, in urgency order, with "everything that needs action"
 * offered first because it is what the page is usually opened for.
 */
export function filterChips(counts = {}, awaiting = 0) {
  const actionable = ACTIONABLE_STATES.reduce((sum, s) => sum + (counts[s] || 0), 0);
  return [
    { value: '', label: 'All products', count: null },
    { value: 'actionable', label: 'Needs action', count: actionable, solid: 'var(--exp-critical-solid)' },
    ...EXPIRY_STATES.map((s) => ({
      value: s,
      label: expiryMeta(s).short,
      count: counts[s] || 0,
      solid: expiryMeta(s).solid,
    })),
    // Not an expiry state: stock that has been pulled and is waiting on the
    // supplier. It has no date to be near, but it must stay findable — it is
    // the half-finished step of the rotation.
    { value: AWAITING, label: 'Awaiting delivery', count: awaiting, solid: 'var(--color-primary-500)' },
  ];
}
